import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { useFriends } from '@/features/social/hooks/useFriends'
import { useInviteToChallenge } from '../hooks/useChallenges'

interface ChallengeInviteFormProps {
  readonly challengeId: string
}

/** On n'invite que des amis : c'est ce que le serveur accepte (§ 6.3). */
export function ChallengeInviteForm({ challengeId }: ChallengeInviteFormProps) {
  const { t } = useTranslation()
  const [userId, setUserId] = useState('')
  const { data } = useFriends()
  const invite = useInviteToChallenge()

  const friends = data?.friends ?? []

  if (friends.length === 0) {
    return null
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (userId === '') return

    invite.mutate({ challengeId, userId }, { onSuccess: () => setUserId('') })
  }

  const selectId = `invite-${challengeId}`

  return (
    <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
      <label htmlFor={selectId} className="sr-only">
        {t('challenges.invite_label')}
      </label>
      <select
        id={selectId}
        value={userId}
        onChange={(event) => setUserId(event.target.value)}
        className="rounded-lg border border-surface-200 px-2 py-1.5 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
      >
        <option value="">{t('challenges.invite_placeholder')}</option>
        {friends.map((friend) => (
          <option key={friend.id} value={friend.id}>
            {friend.username}
          </option>
        ))}
      </select>

      <Button type="submit" size="sm" disabled={userId === ''} isLoading={invite.isPending}>
        {t('challenges.invite')}
      </Button>

      {invite.isError && (
        <span className="text-xs text-mindeia-error">{t('challenges.invite_error')}</span>
      )}
    </form>
  )
}
